import { useCallback, useRef, useState, type KeyboardEvent } from 'react';

/**
 * Roving tabindex over a 2D grid of controls — the app's first.
 *
 * `layout` is the grid as rendered: one array per table row, one entry per
 * column, holding the cell's stable key or `null` where the cell has nothing
 * focusable (a "No team" heading, a disabled heading over an empty team, a
 * message row). Keys are stable across refetches (built from ids, never from
 * indices), so focus survives the grid re-rendering under it.
 *
 * Exactly one cell is in the tab order: the last one focused while it still
 * exists, otherwise the first focusable cell. Arrow keys move one cell and skip
 * over `null`s in the direction of travel; Home / End jump to the first / last
 * cell in the row; Ctrl+Home / Ctrl+End (or ⌘ on macOS) to the first / last
 * cell in the grid. Space is left alone: it is the native checkbox toggle.
 *
 * The keydown handler belongs on the `<table>`, never on a `<tr>` (see
 * docs/solutions/ui-bugs/interactive-tr-no-aria-role-missing-focus-trap-2026-05-07.md).
 */
export interface RovingGrid {
  /** The one cell key that carries `tabIndex={0}`; null when nothing is focusable. */
  tabStopKey: string | null;
  /** A ref callback that registers the control rendered for `key`. */
  register: (key: string) => (el: HTMLElement | null) => void;
  /** Call from the control's `onFocus`, so a click or Tab moves the tab stop too. */
  onCellFocus: (key: string) => void;
  /** Spread onto the grid's `<table>`. */
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  /** Move the tab stop to `key` and focus it — e.g. returning focus after a dialog. */
  focusCell: (key: string) => void;
}

interface Position {
  r: number;
  c: number;
}

function locate(layout: readonly (readonly (string | null)[])[], key: string): Position | null {
  for (let r = 0; r < layout.length; r++) {
    const c = layout[r]?.indexOf(key) ?? -1;
    if (c >= 0) return { r, c };
  }
  return null;
}

function firstKey(layout: readonly (readonly (string | null)[])[]): string | null {
  for (const row of layout) for (const k of row) if (k !== null) return k;
  return null;
}

function lastKey(layout: readonly (readonly (string | null)[])[]): string | null {
  for (let r = layout.length - 1; r >= 0; r--) {
    const row = layout[r] ?? [];
    for (let c = row.length - 1; c >= 0; c--) {
      const k = row[c];
      if (k != null) return k;
    }
  }
  return null;
}

/** Walk from `from` by `step` until a non-null key, or null off the edge. */
function walk(
  layout: readonly (readonly (string | null)[])[],
  from: Position,
  dr: number,
  dc: number,
): string | null {
  let { r, c } = from;
  for (;;) {
    r += dr;
    c += dc;
    if (r < 0 || r >= layout.length) return null;
    const row = layout[r] ?? [];
    if (c < 0 || c >= row.length) return null;
    const k = row[c];
    if (k != null) return k;
  }
}

function rowEdge(row: readonly (string | null)[], fromEnd: boolean): string | null {
  const cells = fromEnd ? [...row].reverse() : row;
  return cells.find((k): k is string => k !== null) ?? null;
}

export function useRovingGrid(layout: readonly (readonly (string | null)[])[]): RovingGrid {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const elements = useRef(new Map<string, HTMLElement>());

  const tabStopKey =
    activeKey !== null && locate(layout, activeKey) !== null ? activeKey : firstKey(layout);

  const register = useCallback(
    (key: string) => (el: HTMLElement | null) => {
      if (el) elements.current.set(key, el);
      else elements.current.delete(key);
    },
    [],
  );

  const focusCell = useCallback((key: string) => {
    setActiveKey(key);
    // The element may be about to re-render (a refetch after a dialog); focus
    // once React has committed.
    queueMicrotask(() => elements.current.get(key)?.focus());
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const key = (e.target as HTMLElement).dataset['cellKey'];
    if (!key) return;
    const at = locate(layout, key);
    if (!at) return;
    const row = layout[at.r] ?? [];
    const toGridEdge = e.ctrlKey || e.metaKey;
    let next: string | null;
    switch (e.key) {
      case 'ArrowRight':
        next = walk(layout, at, 0, 1);
        break;
      case 'ArrowLeft':
        next = walk(layout, at, 0, -1);
        break;
      case 'ArrowDown':
        next = walk(layout, at, 1, 0);
        break;
      case 'ArrowUp':
        next = walk(layout, at, -1, 0);
        break;
      case 'Home':
        next = toGridEdge ? firstKey(layout) : rowEdge(row, false);
        break;
      case 'End':
        next = toGridEdge ? lastKey(layout) : rowEdge(row, true);
        break;
      default:
        return;
    }
    // Consume the key even at an edge, so the scroll container does not jump
    // while focus stays put.
    e.preventDefault();
    if (next === null || next === key) return;
    setActiveKey(next);
    elements.current.get(next)?.focus();
  };

  return { tabStopKey, register, onCellFocus: setActiveKey, onKeyDown, focusCell };
}
